/**
 * catalogue.ts — STAFF FORK of portal-subscriber/src/lib/catalogue.ts (Common Catalogue copy-fork,
 * Structure Guide §9). Client for `GET /portal/catalogue` (ADR-085 D1): the one price list every
 * surface reads — products, the three terms at rack, and the Early Adopter overlay, resolved
 * server-side so no client ever holds a price.
 *
 * ⭐ WHY THE STAFF CONSOLE NEEDS IT (Jeff, 6 October 2026: the 25% three-month price "consistent
 * across the entire site … onboarding, app, and ops"). The staff-mounted OBW's plan and payment
 * steps priced from a static table — 50% off on BOTH committed terms, rounded down ($99/mo) —
 * after the catalogue had moved the 3-month term to 25% ($149.25/mo). A consultant walking a
 * customer through sign-up quoted a price the customer would never be billed.
 *
 * ⚠ WHAT DIFFERS FROM THE SUBSCRIBER COPY, AND WHY.
 *   - The ORIGIN is passed in, not read from a session. The staff OBW is mount-blind: the zone a
 *     journey targets arrives as seeded data (`portalOriginFor(data)` — see ../obw/portal-origin),
 *     and the catalogue is per-zone data (ADR-120 P2, ADR-124 D7). A US journey prices from the US
 *     table.
 *   - No sessionStorage cache. A consultant opens a handful of journeys a day; one fetch per origin
 *     per page load (the module cache below) is enough, and it keeps a zone's edited price from
 *     outliving a reload on the console.
 *
 * The endpoint is T0 PUBLIC (pricing is public marketing data, CORS `*`), so this is a plain fetch
 * with no auth header — the staff pool's token would be rejected by the subscriber authorizer
 * anyway (ADR-018).
 */

export type CatalogueProductId = 'nexagrowth' | 'nexasuccess' | 'nexacontext';
export type CatalogueTerm = 'monthly' | 'quarterly' | 'annual';
export type TierCode = 'T1' | 'T2' | 'T3';

/** Per-term price row. `sub` is the full charge for the term (ex-tax); `monthlyEquivalent` is sub
 *  spread per month for display. The optional `ea` overlay is the Early Adopter price for the term. */
export interface CatalogueTermPricing {
  sub: number;
  monthlyEquivalent: number;
  ea?: {
    sub: number;
    monthlyEquivalent: number;
    durationMonths: number;
  };
}

export interface CatalogueProduct {
  product: CatalogueProductId;
  name: string;
  tierCode: TierCode;
  /** false = defined-not-priced (NexaContext) — surfaces as "Coming soon". */
  priced: boolean;
  /** Empty object when priced === false. */
  terms: Partial<Record<CatalogueTerm, CatalogueTermPricing>>;
}

export interface Catalogue {
  products: CatalogueProduct[];
  currency: string;
  taxNote?: string;
  version?: string;
}

/** One fetch per zone origin per page load; a failure clears its entry so a retry can succeed. */
const cataloguePromises = new Map<string, Promise<Catalogue>>();

export function fetchCatalogue(origin: string): Promise<Catalogue> {
  let promise = cataloguePromises.get(origin);
  if (!promise) {
    promise = (async () => {
      const res = await fetch(`${origin}/portal/catalogue`);
      if (!res.ok) throw new Error(`catalogue fetch failed: HTTP ${res.status}`);
      const body = (await res.json()) as Catalogue;
      if (!Array.isArray(body?.products)) throw new Error('catalogue fetch failed: malformed body');
      return body;
    })().catch((err: unknown) => {
      cataloguePromises.delete(origin);
      throw err;
    });
    cataloguePromises.set(origin, promise);
  }
  return promise;
}

/** Test seam — every test starts cold. */
export function resetCatalogueCache(): void {
  cataloguePromises.clear();
}
